import { Users, Server, Database, ChevronUp, ChevronDown, Sparkles, Minimize2, ChevronLeft, ChevronRight, Focus, Check, UserCog, Cloud, Monitor, Archive, Cylinder, Menu, Shrink, LayoutGrid } from 'lucide-react';
import { Button } from '../../../ui/common-ui/button';
import { useGraphStore } from '../store/graphStore';
import { useState, useEffect, useRef } from 'react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../ui/common-ui/popover";
import { useCloudAccounts } from '../hooks/useCloudAccounts';
import { useUICollapse } from '../hooks/useUICollapse';
import { cn } from '../lib/utils';

export function CollapsibleControlBar() {
  const { isCollapsed, setHoveredElement, hoveredElement } = useUICollapse();
  const isHovered = hoveredElement === 'controlbar';

  const [selectedQuestion, setSelectedQuestion] = useState<string>('');
  const [isOpen, setIsOpen] = useState(false);
  const [saDropdownOpen, setSaDropdownOpen] = useState(false);
  const [resourceDropdownOpen, setResourceDropdownOpen] = useState(false);
  const [selectedSaType, setSelectedSaType] = useState<string | null>(null);
  const [selectedResourceType, setSelectedResourceType] = useState<string | null>(null);

  const hasAnyPopoverOpen = isOpen || saDropdownOpen || resourceDropdownOpen;
  const showExpanded = !isCollapsed || isHovered || hasAnyPopoverOpen;

  const { selectedCloud } = useCloudAccounts();
  const {
    activeConnectionType,
    activeConnectionSubFilter,
    setActiveConnectionType,
    collapseAll,
    nextGraphPage,
    previousGraphPage,
    canGoNext,
    canGoPrevious,
    focusMode,
    toggleFocusMode,
    setFocusedGraph,
    applySecurityQuestion,
    triggerBeautifyLayout,
    activeMode,
    policyCoverageView,
  } = useGraphStore();
  const [activeFilter, setActiveFilter] = useState<string>('users');
  const previousCloudSelectionRef = useRef<string | null>(null);
  const cloudSelectionKey =
    selectedCloud.cloudType && selectedCloud.cloudId
      ? `${selectedCloud.cloudType}:${selectedCloud.cloudId}`
      : null;

  // Keep the toolbar UI aligned with store-driven changes such as graph quick links.
  useEffect(() => {
    setActiveFilter(activeConnectionType || 'users');
    setSelectedSaType(
      activeConnectionType === 'sa' ? activeConnectionSubFilter || null : null,
    );
    setSelectedResourceType(
      activeConnectionType === 'resources'
        ? activeConnectionSubFilter || null
        : null,
    );
  }, [activeConnectionType, activeConnectionSubFilter]);

  // Reset to Users only when the selected cloud actually changes after initial load.
  useEffect(() => {
    if (!cloudSelectionKey) return;

    const previousCloudSelection = previousCloudSelectionRef.current;
    previousCloudSelectionRef.current = cloudSelectionKey;

    if (previousCloudSelection === null) {
      return;
    }

    if (previousCloudSelection === cloudSelectionKey) {
      return;
    }

    setSelectedSaType(null);
    setSelectedResourceType(null);
    setSaDropdownOpen(false);
    setResourceDropdownOpen(false);
    setActiveFilter('users');
    setActiveConnectionType('users');
  }, [cloudSelectionKey, setActiveConnectionType]);

  // Force-close all popovers when the graph collapses the UI
  useEffect(() => {
    if (!isCollapsed) return;
    setIsOpen(false);
    setSaDropdownOpen(false);
    setResourceDropdownOpen(false);
  }, [isCollapsed]);

  const questionIdToIndex: Record<string, number> = {
    'most-risky-entities': 1,
    'dormant-users': 2,
    'dormant-service-accounts': 3,
    'most-active-entities': 4,
    'most-used-resources': 5,
    'over-privileged-accounts': 6,
    'recent-anomalies': 7,
  };

  const closeFocusMode = () => {
    if (focusMode) {
      toggleFocusMode();
      setFocusedGraph(null);
    }
  };

  const handleSaTypeSelect = (type: string) => {
    closeFocusMode();
    setSelectedSaType(type);
    setActiveFilter('sa');
    setActiveConnectionType('sa', type);
    setSaDropdownOpen(false);
  };

  const handleResourceTypeSelect = (type: string) => {
    closeFocusMode();
    setSelectedResourceType(type);
    setActiveFilter('resources');
    setActiveConnectionType('resources', type);
    setResourceDropdownOpen(false);
  };

  const saOptions = [
    { id: 'user-managed', label: 'User Managed', icon: UserCog },
    { id: 'cloud-managed', label: 'Cloud Managed', icon: Cloud },
  ];

  const getResourceOptions = () => {
    const cloudType = selectedCloud.cloudType;
    if (cloudType === 'AWS') {
      return [
        { id: 'ec2', label: 'EC2', icon: Monitor },
        { id: 'rds', label: 'RDS', icon: Cylinder },
        { id: 's3', label: 'S3 Bucket', icon: Archive },
      ];
    }
    return [
      { id: 'vm', label: 'Virtual Machine', icon: Monitor },
      { id: 'storage', label: 'Storage', icon: Archive },
      { id: 'database', label: 'Database', icon: Cylinder },
    ];
  };

  const showSaDropdown = selectedCloud.cloudType === 'GCP';

  const getSaButtonLabel = () => {
    if (selectedCloud.cloudType === 'AWS') return 'IAM Roles';
    return 'Service Accounts';
  };

  const getSaIcon = () => {
    if (!selectedSaType) return Server;
    const option = saOptions.find(o => o.id === selectedSaType);
    return option?.icon || Server;
  };

  const getResourceIcon = () => {
    if (!selectedResourceType) return Cylinder;
    const option = getResourceOptions().find(o => o.id === selectedResourceType);
    return option?.icon || Cylinder;
  };

  const handleSaButtonClick = () => {
    closeFocusMode();
    if (showSaDropdown) {
      setSaDropdownOpen(!saDropdownOpen);
      setResourceDropdownOpen(false);
    } else {
      setActiveFilter('sa');
      setActiveConnectionType('sa', undefined);
      setSelectedSaType(null);
      setSelectedResourceType(null);
      setSaDropdownOpen(false);
      setResourceDropdownOpen(false);
    }
  };

  const SaIcon = getSaIcon();
  const ResourceIcon = getResourceIcon();

  const frequentQuestions = [
    { id: 'most-risky-entities', label: 'Most Risky Entities', description: 'Entities with most alerts attached', icon: Users },
    { id: 'dormant-users', label: 'Dormant Users', description: 'Users with zero activity', icon: Users },
    { id: 'dormant-service-accounts', label: 'Dormant Service Accounts', description: 'Service accounts with zero activity', icon: Server },
    { id: 'most-active-entities', label: 'Most Active Entities', description: 'Entities with highest activity levels', icon: Users },
    { id: 'most-used-resources', label: 'Most Used Resources', description: 'Resources sorted by activity level', icon: Database },
    { id: 'over-privileged-accounts', label: 'Over Privileged Accounts', description: 'Accounts with most roles attached', icon: Users },
    { id: 'recent-anomalies', label: 'Recent Alerts', description: 'Entities with most recent alerts', icon: Server },
  ];

  return (
    <div className="fixed bottom-2 md:bottom-6 left-0 right-0 z-50 flex justify-center pointer-events-none">
      {/* Centered wrapper: the toolbar alone defines the centering. The helper
          buttons are absolutely positioned (out of flow) so mounting/unmounting
          them never reflows — and thus never shifts — the centered toolbar
          during the expand/collapse animation. */}
      <div className="relative flex items-center pointer-events-auto">
        {/* Toolbar container with its own hover */}
        <div
          onMouseEnter={() => setHoveredElement('controlbar')}
          onMouseLeave={() => setHoveredElement(null)}
        >
        <div className={cn(
          "bg-card shadow-2xl border border-border/50 overflow-hidden transition-all duration-300 ease-out",
          showExpanded
            ? "w-[calc(100vw-1rem)] md:w-[calc(100vw-2rem)] lg:w-[800px] max-w-[95vw] rounded-2xl overflow-hidden"
            : "w-48 rounded-xl overflow-hidden"
        )}>
          {/* Collapsed State */}
          <div className={cn(
            "px-6 py-3 flex items-center gap-4 cursor-pointer hover:bg-muted/50 transition-all duration-300 ease-out",
            showExpanded ? "opacity-0 absolute pointer-events-none" : "opacity-100"
          )}>
            <Menu className="w-5 h-5 text-primary" />
            <div className="flex items-center gap-2">
              <div className={cn(
                "w-2 h-2 rounded-full",
                activeFilter === 'users' ? "bg-blue-500" :
                  activeFilter === 'sa' ? "bg-purple-500" :
                    "bg-emerald-500"
              )} />
              <span className="text-sm font-medium text-foreground capitalize whitespace-nowrap">
                {activeFilter === 'sa' ? getSaButtonLabel() : activeFilter}
              </span>
            </div>
            <ChevronUp className="w-4 h-4 text-muted-foreground" />
          </div>

          {/* Expanded State */}
          <div className={cn(
            "p-2 md:p-4 transition-opacity duration-300 ease-out",
            showExpanded ? "opacity-100" : "opacity-0 absolute pointer-events-none"
          )}>
            <div className="space-y-2 md:space-y-3">
              {/* Filter Tabs and Navigation Controls */}
              <div className="flex items-center gap-1 md:gap-2 pb-2 md:pb-3 overflow-hidden">
                {/* Users Button */}
                <Button
                  variant={activeFilter === 'users' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => {
                    closeFocusMode();
                    setActiveFilter('users');
                    setActiveConnectionType('users');
                    setSelectedSaType(null);
                    setSelectedResourceType(null);
                  }}
                  className="gap-1 md:gap-1.5 flex-shrink-0"
                >
                  <Users className="w-3 md:w-3.5 h-3 md:h-3.5" />
                  <span className="text-xs hidden sm:inline">Users</span>
                </Button>

                {/* Service Accounts / IAM Roles Button */}
                {showSaDropdown ? (
                  <Popover open={saDropdownOpen} onOpenChange={setSaDropdownOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant={activeFilter === 'sa' ? 'default' : 'ghost'}
                        size="sm"
                        className="gap-1 md:gap-1.5 flex-shrink-0"
                      >
                        <SaIcon className="w-3 md:w-3.5 h-3 md:h-3.5" />
                        <span className="text-xs hidden sm:inline">{getSaButtonLabel()}</span>
                        <ChevronDown className={`w-3 h-3 transition-transform ${saDropdownOpen ? 'rotate-180' : ''}`} />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      side="top"
                      align="start"
                      sideOffset={8}
                      className="z-50 w-48 p-1.5 bg-card border border-border shadow-xl"
                    >
                      {saOptions.map((option) => {
                        const OptionIcon = option.icon;
                        const isSelected = selectedSaType === option.id;
                        return (
                          <button
                            key={option.id}
                            onClick={() => handleSaTypeSelect(option.id)}
                            className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg transition-colors text-left ${isSelected
                              ? 'bg-primary/15 text-foreground'
                              : 'hover:bg-muted text-foreground'
                              }`}
                          >
                            <OptionIcon className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm font-medium">{option.label}</span>
                            {isSelected && <Check className="w-4 h-4 ml-auto text-primary" />}
                          </button>
                        );
                      })}
                    </PopoverContent>
                  </Popover>
                ) : (
                  <Button
                    variant={activeFilter === 'sa' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={handleSaButtonClick}
                    className="gap-1 md:gap-1.5 flex-shrink-0"
                  >
                    <Server className="w-3 md:w-3.5 h-3 md:h-3.5" />
                    <span className="text-xs hidden sm:inline">{getSaButtonLabel()}</span>
                  </Button>
                )}

                {/* Resources with Dropdown */}
                <Popover open={resourceDropdownOpen} onOpenChange={setResourceDropdownOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant={activeFilter === 'resources' ? 'default' : 'ghost'}
                      size="sm"
                      className="gap-1 md:gap-1.5 flex-shrink-0"
                    >
                      <ResourceIcon className="w-3 md:w-3.5 h-3 md:h-3.5" />
                      <span className="text-xs hidden sm:inline">Resources</span>
                      <ChevronDown className={`w-3 h-3 transition-transform ${resourceDropdownOpen ? 'rotate-180' : ''}`} />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    side="top"
                    align="start"
                    sideOffset={8}
                    className="z-50 w-48 p-1.5 bg-card border border-border shadow-xl"
                  >
                    {getResourceOptions().map((option) => {
                      const OptionIcon = option.icon;
                      const isSelected = selectedResourceType === option.id;
                      return (
                        <button
                          key={option.id}
                          onClick={() => handleResourceTypeSelect(option.id)}
                          className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg transition-colors text-left ${isSelected
                            ? 'bg-primary/15 text-foreground'
                            : 'hover:bg-muted text-foreground'
                            }`}
                        >
                          <OptionIcon className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm font-medium">{option.label}</span>
                          {isSelected && <Check className="w-4 h-4 ml-auto text-primary" />}
                        </button>
                      );
                    })}
                  </PopoverContent>
                </Popover>

                <div className="flex-1" />

                {/* Focus Mode Toggle */}
                <Button
                  variant={focusMode ? "ghost" : "default"}
                  size="sm"
                  disabled={activeMode === 'policy-coverage' && policyCoverageView === 'policy'}
                  onClick={() => {
                    toggleFocusMode();
                    if (!focusMode) {
                      setFocusedGraph(null);
                      collapseAll();
                    }
                  }}
                  className="flex-shrink-0"
                  title={activeMode === 'policy-coverage' && policyCoverageView === 'policy'
                    ? "Focus mode is available in entity view"
                    : focusMode ? "Multi-Select Mode (Current)" : "Focus Mode"}
                >
                  <Focus className="w-3 md:w-3.5 h-3 md:h-3.5" />
                </Button>

                {/* Navigation Buttons */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    closeFocusMode();
                    previousGraphPage();
                  }}
                  disabled={!canGoPrevious()}
                  className="gap-1 flex-shrink-0"
                  title="Previous Graphs"
                >
                  <ChevronLeft className="w-3 md:w-3.5 h-3 md:h-3.5" />
                  <span className="text-xs hidden md:inline">Prev</span>
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    closeFocusMode();
                    nextGraphPage();
                  }}
                  disabled={!canGoNext()}
                  className="gap-1 flex-shrink-0"
                  title="Next Graphs"
                >
                  <span className="text-xs hidden md:inline">Next</span>
                  <ChevronRight className="w-3 md:w-3.5 h-3 md:h-3.5" />
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    closeFocusMode();
                    collapseAll();
                  }}
                  className="gap-1 md:gap-1.5 flex-shrink-0"
                  title="Collapse All Nodes"
                >
                  <Minimize2 className="w-3 md:w-3.5 h-3 md:h-3.5" />
                  <span className="text-xs hidden md:inline">Collapse</span>
                </Button>

                {/* Beautify Button - only enabled in focus mode */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => triggerBeautifyLayout()}
                  disabled={focusMode}
                  className="gap-1 md:gap-1.5 flex-shrink-0"
                  title={focusMode ? "Enter Focus Mode to beautify" : "Beautify Graph Layout"}
                >
                  <LayoutGrid className="w-3 md:w-3.5 h-3 md:h-3.5" />
                  <span className="text-xs hidden md:inline">Beautify</span>
                </Button>
              </div>

              {/* Question Selector */}
              {activeMode !== 'policy-coverage' && <div className="flex gap-2 items-center">
                <Popover open={isOpen} onOpenChange={setIsOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="flex-1 justify-between h-12 md:h-14 text-left font-normal group hover:border-primary hover:bg-primary/5 transition-all shadow-sm hover:shadow-md"
                    >
                      <div className="flex items-center gap-2 md:gap-3 min-w-0">
                        <div className="p-1.5 md:p-2 rounded-lg bg-primary/10 group-hover:bg-primary/15 transition-colors flex-shrink-0">
                          <Sparkles className="w-3.5 md:w-4 h-3.5 md:h-4 text-primary" />
                        </div>
                        <div className="flex flex-col gap-0.5 min-w-0">
                          <span className={`text-xs md:text-sm font-medium truncate ${selectedQuestion ? "text-foreground" : "text-muted-foreground"}`}>
                            {selectedQuestion || "What would you like to explore?"}
                          </span>
                          {!selectedQuestion && (
                            <span className="text-[10px] md:text-xs text-muted-foreground hidden sm:block">Choose from 7 common security questions</span>
                          )}
                        </div>
                      </div>
                      <ChevronUp className={`w-4 md:w-5 h-4 md:h-5 text-muted-foreground transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-[calc(100vw-2rem)] md:w-[650px] lg:w-[750px] p-0 bg-card border border-border shadow-2xl"
                    align="center"
                    side="top"
                    sideOffset={12}
                  >
                    <div className="max-h-[420px] overflow-y-auto custom-scrollbar p-2">
                      <div className="grid gap-1">
                        {frequentQuestions.map((question) => {
                          const Icon = question.icon;
                          const isSelected = selectedQuestion === question.label;
                          return (
                            <Button
                              key={question.id}
                              variant="ghost"
                              className={`w-full justify-start h-auto py-3.5 px-3 transition-all group ${isSelected ? 'bg-primary/15 hover:bg-primary/20' : 'hover:bg-primary/10'
                                }`}
                              onClick={() => {
                                closeFocusMode();
                                setSelectedQuestion(question.label);
                                setIsOpen(false);
                                const questionIndex = questionIdToIndex[question.id];
                                if (questionIndex) {
                                  applySecurityQuestion(questionIndex);
                                }
                              }}
                            >
                              <div className="flex items-start gap-3 w-full">
                                <div className={`mt-0.5 p-2.5 rounded-lg transition-colors ${isSelected
                                  ? 'bg-primary/25 group-hover:bg-primary/30'
                                  : 'bg-primary/10 group-hover:bg-primary/20'
                                  }`}>
                                  <Icon className="w-4 h-4 text-primary" />
                                </div>
                                <div className="flex-1 text-left">
                                  <span className={`block text-sm font-medium ${isSelected ? 'text-primary' : 'text-foreground'
                                    }`}>
                                    {question.label}
                                  </span>
                                  <span className="block text-xs text-muted-foreground mt-0.5">
                                    {question.description}
                                  </span>
                                </div>
                                {isSelected && (
                                  <Check className="w-4 h-4 text-primary mt-1" />
                                )}
                              </div>
                            </Button>
                          );
                        })}
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>}
            </div>
          </div>
        </div>
      </div>
        {!showExpanded && (
          <div className="absolute left-full top-0 bottom-0 ml-3 flex items-center gap-3">
            {/* Previous Button */}
          <button
            onClick={() => {
              if (!focusMode) {
                toggleFocusMode();
                setFocusedGraph(null);
              }
              previousGraphPage();
            }}
            disabled={!canGoPrevious()}
            className="bg-card shadow-xl border border-border/50 rounded-xl p-3 hover:bg-muted/50 transition-all duration-300 ease-out disabled:opacity-40 disabled:cursor-not-allowed"
            title="Previous Graphs"
          >
            <ChevronLeft className="w-5 h-5 text-primary" />
          </button>

          {/* Next Button */}
          <button
            onClick={() => {
              if (!focusMode) {
                toggleFocusMode();
                setFocusedGraph(null);
              }
              nextGraphPage();
            }}
            disabled={!canGoNext()}
            className="bg-card shadow-xl border border-border/50 rounded-xl p-3 hover:bg-muted/50 transition-all duration-300 ease-out disabled:opacity-40 disabled:cursor-not-allowed"
            title="Next Graphs"
          >
            <ChevronRight className="w-5 h-5 text-primary" />
          </button>

          {/* Collapse/Shrink Button */}
          <button
            onClick={() => {
              if (!focusMode) {
                toggleFocusMode();
                setFocusedGraph(null);
              }
              collapseAll();
            }}
            className="bg-card shadow-xl border border-border/50 rounded-xl p-3 hover:bg-muted/50 transition-all duration-300 ease-out"
            title="Collapse All Nodes"
          >
            <Shrink className="w-5 h-5 text-primary" />
          </button>

          {/* Beautify Button (collapsed state) */}
          <button
            onClick={() => triggerBeautifyLayout()}
            disabled={focusMode}
            className="bg-card shadow-xl border border-border/50 rounded-xl p-3 hover:bg-muted/50 transition-all duration-300 ease-out disabled:opacity-40 disabled:cursor-not-allowed"
            title={focusMode ? "Enter Focus Mode to beautify" : "Beautify Graph Layout"}
          >
            <LayoutGrid className="w-5 h-5 text-primary" />
          </button>
          </div>
        )}
      </div>
    </div>
  );
}
